import { useInfiniteQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@doger/ui/components/ui/badge';
import { Button } from '@doger/ui/components/ui/button';
import { Skeleton } from '@doger/ui/components/ui/skeleton';
import type { AIModel } from '@doger/core/modules/chat';
import { ModelToView } from '@doger/core/modules/chat';

import { $$privateApi } from '@doger/admin/shared/api';

import styles from './user-chats.module.scss';

const CHATS_PER_PAGE = 20;

export const UserChatsPage = memo(() => {
  const userId = useParams<{ userId: string }>().userId!;
  const navigate = useNavigate();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ['user-chats', userId],
    queryFn: ({ pageParam = 0 }) =>
      $$privateApi.findUserChats({
        userId,
        take: CHATS_PER_PAGE,
        skip: pageParam * CHATS_PER_PAGE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      // If we got fewer results than requested, we've reached the end
      lastPage.length === CHATS_PER_PAGE ? allPages.length : undefined,
    staleTime: 0,
  });

  const { ref, inView } = useInView({
    threshold: 0,
  });

  const loadMore = useCallback(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Load more when the last item comes into view
  useEffect(() => {
    loadMore();
  }, [loadMore]);

  const allChats = useMemo(
    () => data?.pages.flatMap((page) => page) ?? [],
    [data]
  );

  const columns = useMemo<ColumnDef<(typeof allChats)[number]>[]>(
    () => [
      {
        accessorKey: 'sessionId',
        header: 'Session ID',
        cell: ({ row }) => (
          <div className={styles.sessionId}>{row.original.sessionId}</div>
        ),
      },
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => (
          <div className={styles.chatTitle} title={row.original.title}>
            {row.original.title}
          </div>
        ),
      },
      {
        accessorKey: 'appName',
        header: 'Model',
        cell: ({ row }) => (
          <div className={styles.chatTitle}>
            {ModelToView.toView(row.original.app as AIModel).name}
          </div>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        cell: ({ row }) => (
          <div className={styles.lastUpdated}>
            {format(new Date(row.original.createdAt), 'MMM dd, yyyy HH:mm')}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const chat = row.original;

          return (
            <div className={styles.chatStatus}>
              <div className={styles.statusContainer}>
                <Badge
                  variant={chat.status === 'ACTIVE' ? 'default' : 'outline'}
                  className={
                    chat.status === 'ACTIVE'
                      ? styles.activeBadge
                      : styles.deletedBadge
                  }
                >
                  {chat.status}
                </Badge>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className={styles.actions}>
            {!!row.original.session && (
              <Button
                variant='outlined'
                size='sm'
                onClick={() =>
                  navigate(`/users/${userId}/chats/${row.original.sessionId}`)
                }
                palette='ghost'
              >
                View Chat
              </Button>
            )}
          </div>
        ),
      },
    ],
    [userId]
  );

  const table = useReactTable({
    data: allChats,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <div className={styles.tableContainer}>
        <table className={styles.chatsTable}>
          <thead>
            <tr>
              <th>Session ID</th>
              <th>Title</th>
              <th>Model</th>
              <th>Last Updated</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, index) => (
              <tr key={index}>
                <td>
                  <Skeleton className={styles.skeleton} />
                </td>
                <td>
                  <Skeleton className={styles.skeleton} />
                </td>
                <td>
                  <Skeleton className={styles.skeleton} />
                </td>
                <td>
                  <Skeleton className={styles.skeleton} />
                </td>
                <td>
                  <Skeleton className={styles.skeleton} />
                </td>
                <td>
                  <Skeleton className={styles.skeleton} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>Error loading chats: {error.message}</div>
    );
  }

  if (allChats.length === 0) {
    return (
      <div className={styles.noChats}>
        <p>No chats found for this user.</p>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <table className={styles.chatsTable}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
          {/* Intersection observer target for infinite scroll */}
          {hasNextPage && (
            <tr ref={ref}>
              <td colSpan={columns.length} className={styles.loadingRow}>
                {isFetchingNextPage ? (
                  <div className={styles.loadingMore}>
                    <Skeleton className={styles.skeleton} />
                    <span>Loading more chats...</span>
                  </div>
                ) : null}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
});
